<template>
  <div class="relative">
    <Input
      v-model="searchInput"
      :class="{ 'border-red-400': !isInputValid && searchInput }"
      placeholder="Select state"
      icon="i-heroicons-magnifying-glass"
      @click="showSuggestions = true"
      @update:model-value="onSearch"
    />
    <div
      v-if="isClient && showSuggestions && filteredStates.length > 0"
      class="suggestions-box"
    >
      <button
        v-for="state in filteredStates"
        :key="state.code"
        class="suggestion-item"
        @click.stop="selectState(state)"
      >
        {{ state.label }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { stateOptions } from '~/utils/states'

export interface StateOption {
  code: string
  label: string
}
const props = defineProps<{
  initialState?: string
  statesCities?: Record<string, any[]>
}>()
const emit = defineEmits<{
  'update:selected': [StateOption | null]
  'update:input': [string]
  'update:isValid': [boolean]
  'update:exactMatch': [boolean]
}>()
const companyStates = computed(() => {
  if (!props.statesCities) return []
  return Object.keys(props.statesCities)
})
const availableStateOptions = computed(() => {
  if (!companyStates.value.length) return stateOptions
  const companyStateSet = new Set(
    companyStates.value.map(s => s.toLowerCase())
  )
  return stateOptions
    .filter(state => companyStateSet.has(state.label.toLowerCase()))
    .sort((a, b) => a.label.localeCompare(b.label))
})
const findInitialState = (
  initialValue: string | undefined
): StateOption | null => {
  if (!initialValue) return null
  let state = stateOptions.find(s => s.code === initialValue)
  if (!state) {
    state = stateOptions.find(
      s => s.label.toLowerCase() === initialValue.toLowerCase()
    )
  }
  return state || null
}
const selectedState = ref<StateOption | null>(
  findInitialState(props.initialState)
)
const searchInput = ref(selectedState.value?.label || '')
const showSuggestions = ref(false)
const isClient = ref(false)

onMounted(() => {
  isClient.value = true
})

const isInputValid = computed(() => {
  if (!searchInput.value.trim()) return true
  const normalizedInput = searchInput.value.toLowerCase().trim()
  const matchInAvailable = availableStateOptions.value.find(
    state => state.label.toLowerCase().trim() === normalizedInput
  )
  if (matchInAvailable) return true
  if (companyStates.value.length === 0) {
    return !!stateOptions.find(
      state => state.label.toLowerCase().trim() === normalizedInput
    )
  }
  return false
})
watch(isInputValid, (valid) => {
  emit('update:isValid', valid)
})
watch(
  () => props.initialState,
  (newValue) => {
    if (newValue) {
      const state = findInitialState(newValue)
      if (state) {
        selectedState.value = state
        searchInput.value = state.label
      }
    }
  },
  { immediate: true }
)
const filteredStates = computed(() => {
  const query = searchInput.value?.toLowerCase() || ''
  const statesToSearch = availableStateOptions.value
  if (!query) {
    return statesToSearch
  }
  return statesToSearch
    .filter(state => state.label?.toLowerCase().includes(query))
    .slice(0, 10)
})
function selectState(state: StateOption) {
  selectedState.value = state
  searchInput.value = state.label
  showSuggestions.value = false
  emit('update:selected', state)
  emit('update:input', state.label)
  emit('update:isValid', true)
  emit('update:exactMatch', true)
}
function onSearch(value: string) {
  searchInput.value = value
  showSuggestions.value = true
  emit('update:input', value)
  const normalizedInput = value.toLowerCase().trim()
  const exactMatch = availableStateOptions.value.find(
    state => state.label.toLowerCase().trim() === normalizedInput
  )
  emit('update:exactMatch', !!exactMatch)
  emit('update:isValid', !value.trim() || !!exactMatch)
  if (!value) {
    selectedState.value = null
  } else if (exactMatch) {
    selectedState.value = exactMatch
  }
}
onMounted(() => {
  if (import.meta.client) {
    document.addEventListener('click', (event) => {
      const target = event.target as HTMLElement
      if (!target.closest('.suggestions-box') && !target.closest('input')) {
        showSuggestions.value = false
      }
    })
  }
  emit('update:isValid', isInputValid.value)
  emit('update:exactMatch', !!selectedState.value)
})
defineExpose({
  searchInput,
  selectedState,
  isInputValid
})
</script>

<style scoped>
.suggestions-box {
  @apply absolute z-50 w-full mt-1 bg-white border rounded-md shadow-lg max-h-60 overflow-auto;
}

.suggestion-item {
  @apply w-full px-4 py-2 text-left hover:bg-gray-100 flex justify-between items-center;
}
</style>
